import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Alert, Spin } from 'antd';
import {
  DashboardOutlined,
  ProjectOutlined,
  AlertOutlined,
  UserOutlined,
  SafetyOutlined,
  WarningOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const MSPDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/msp/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch MSP dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" tip="Loading MSP Dashboard..." />
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <Alert
        message="Failed to load dashboard data"
        description="Please try refreshing the page"
        type="error"
      />
    );
  }

  const { organizations_count, aggregated_stats, top_alerts, attention_required } = dashboardData;

  const alertColumns = [
    {
      title: 'Organization',
      dataIndex: 'org_name',
      key: 'org_name',
      width: '20%',
    },
    {
      title: 'Project',
      dataIndex: 'project_name',
      key: 'project_name',
      width: '20%',
    },
    {
      title: 'Control',
      dataIndex: 'control_code',
      key: 'control_code',
      width: '10%',
      render: (code) => <Tag color="blue">{code}</Tag>
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: '10%',
      render: (severity) => {
        const color = severity === 'critical' ? 'red' : severity === 'high' ? 'orange' : severity === 'medium' ? 'gold' : 'blue';
        return <Tag color={color}>{severity?.toUpperCase()}</Tag>;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '10%',
      render: (status) => {
        const color = status === 'open' ? 'red' : 'orange';
        return <Tag color={color}>{status}</Tag>;
      }
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '15%',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A'
    }
  ];

  const attentionColumns = [
    {
      title: 'Organization',
      dataIndex: 'org_name',
      key: 'org_name',
      width: '25%',
    },
    {
      title: 'Trust Score',
      dataIndex: 'trust_score',
      key: 'trust_score',
      width: '15%',
      render: (score) => score ? (
        <Tag color={score >= 80 ? 'green' : score >= 70 ? 'orange' : 'red'}>
          {score.toFixed(1)}
        </Tag>
      ) : 'N/A'
    },
    {
      title: 'Active Alerts',
      dataIndex: 'alert_count',
      key: 'alert_count',
      width: '15%',
      render: (count) => <Tag color={count > 10 ? 'red' : 'orange'}>{count}</Tag>
    },
    {
      title: 'Reasons',
      dataIndex: 'reasons',
      key: 'reasons',
      width: '45%',
      render: (reasons) => (
        <div>
          {reasons.map((reason, index) => (
            <Tag key={index} color="red" style={{ marginBottom: 4 }}>
              {reason}
            </Tag>
          ))}
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <h1 style={{ marginBottom: 24 }}>
        <DashboardOutlined /> MSP Dashboard
      </h1>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Organizations"
              value={organizations_count}
              prefix={<ProjectOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Projects"
              value={aggregated_stats.total_projects}
              prefix={<ProjectOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Alerts"
              value={aggregated_stats.total_active_alerts}
              prefix={<AlertOutlined />}
              valueStyle={{ color: aggregated_stats.total_active_alerts > 0 ? '#cf1322' : '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Avg Trust Score"
              value={aggregated_stats.avg_trust_score}
              precision={1}
              prefix={<SafetyOutlined />}
              valueStyle={{
                color: aggregated_stats.avg_trust_score >= 80 ? '#3f8600' :
                       aggregated_stats.avg_trust_score >= 70 ? '#faad14' : '#cf1322'
              }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Open Findings"
              value={aggregated_stats.total_open_findings}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Users"
              value={aggregated_stats.total_users}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Organizations Requiring Attention */}
      {attention_required && attention_required.length > 0 && (
        <Card
          title={<span><WarningOutlined style={{ color: '#faad14' }} /> Organizations Requiring Attention</span>}
          style={{ marginBottom: 24 }}
        >
          <Table
            dataSource={attention_required}
            columns={attentionColumns}
            rowKey="org_id"
            pagination={false}
          />
        </Card>
      )}

      {/* Recent Alerts */}
      <Card title={<span><AlertOutlined /> Recent Alerts (Top 10)</span>}>
        {top_alerts && top_alerts.length > 0 ? (
          <Table
            dataSource={top_alerts}
            columns={alertColumns}
            rowKey="id"
            pagination={false}
          />
        ) : (
          <Alert
            message="No active alerts"
            description="All organizations are operating smoothly"
            type="success"
            showIcon
          />
        )}
      </Card>
    </div>
  );
};

export default MSPDashboard;
